import React, { useCallback, useEffect, useState } from 'react';
import { Banner, Button, Card, EmptyState, Input, Select, Spinner, Switch, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { useProperty } from '../../app/PropertyContext';
import { settingsApi } from './settingsApi';

const DEFAULT_PRICE_PCT = 1;
const DEFAULT_QTY_PCT = 0;

// '' means "use the built-in default"; the API stores null in that case.
const toPayload = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
};
const fromDto = (v) => (v === null || v === undefined ? '' : String(v));

function ToleranceFields({ pricePct, qtyPct, onPrice, onQty, t }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-5)', flexWrap: 'wrap', marginTop: 'var(--space-4)' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 220 }}>
        <span className="appr-doc__ccy-label">{t('Price tolerance (%)')}</span>
        <Input
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={pricePct}
          onChange={(e) => onPrice(e.target.value)}
          placeholder={t('Default: {n}%', { n: DEFAULT_PRICE_PCT })}
          aria-label={t('Price tolerance (%)')}
        />
        <span className="set-section__hint">{t('Billed unit price may differ from the PO price by up to this much.')}</span>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 220 }}>
        <span className="appr-doc__ccy-label">{t('Quantity tolerance (%)')}</span>
        <Input
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={qtyPct}
          onChange={(e) => onQty(e.target.value)}
          placeholder={t('Default: {n}% (exact)', { n: DEFAULT_QTY_PCT })}
          aria-label={t('Quantity tolerance (%)')}
        />
        <span className="set-section__hint">{t('Billed quantity may differ from the received quantity by up to this much.')}</span>
      </label>
    </div>
  );
}

/**
 * Three-way-match tolerances: organization default plus optional per-property override.
 * Invoices whose billed price/quantity deviate beyond these get flagged with a variance
 * and are forced through the full invoice approval chain.
 */
export default function InvoiceMatchingSettingsTab() {
  const toast = useToast();
  const t = useT();
  const { properties, loaded: propsLoaded } = useProperty();

  const [orgPrice, setOrgPrice] = useState('');
  const [orgQty, setOrgQty] = useState('');
  const [orgRequireAttachment, setOrgRequireAttachment] = useState(true);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgSaving, setOrgSaving] = useState(false);

  const [selectedProperty, setSelectedProperty] = useState('');
  const [propOverride, setPropOverride] = useState(false);
  const [propPrice, setPropPrice] = useState('');
  const [propQty, setPropQty] = useState('');
  const [propRequireAttachment, setPropRequireAttachment] = useState(true);
  const [propLoading, setPropLoading] = useState(false);
  const [propSaving, setPropSaving] = useState(false);
  const [propLoaded, setPropLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const org = await settingsApi.getOrg();
        setOrgPrice(fromDto(org?.matchPriceTolerancePercent));
        setOrgQty(fromDto(org?.matchQuantityTolerancePercent));
        setOrgRequireAttachment(org?.requireInvoiceAttachmentOnApprove !== false);
      } catch (err) {
        toast.error(t('Could not load settings'), { description: err?.message });
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [toast, t]);

  const loadProperty = useCallback(async (propertyId) => {
    setSelectedProperty(propertyId);
    setPropLoaded(false);
    if (!propertyId) return;
    setPropLoading(true);
    try {
      const doc = await settingsApi.getPropertyCommercial(propertyId);
      setPropOverride(!!doc?.overrideInvoiceMatching);
      setPropPrice(fromDto(doc?.matchPriceTolerancePercent));
      setPropQty(fromDto(doc?.matchQuantityTolerancePercent));
      setPropRequireAttachment(doc?.requireInvoiceAttachmentOnApprove !== false);
      setPropLoaded(true);
    } catch (err) {
      toast.error(t('Could not load property settings'), { description: err?.message });
    } finally {
      setPropLoading(false);
    }
  }, [toast, t]);

  const saveOrg = async () => {
    setOrgSaving(true);
    try {
      const res = await settingsApi.updateOrgInvoiceMatching({
        matchPriceTolerancePercent: toPayload(orgPrice),
        matchQuantityTolerancePercent: toPayload(orgQty),
        requireInvoiceAttachmentOnApprove: orgRequireAttachment,
      });
      setOrgPrice(fromDto(res?.matchPriceTolerancePercent));
      setOrgQty(fromDto(res?.matchQuantityTolerancePercent));
      setOrgRequireAttachment(res?.requireInvoiceAttachmentOnApprove !== false);
      toast.success(t('Match tolerances saved'));
    } catch (err) {
      toast.error(t('Could not save settings'), { description: err?.message });
    } finally {
      setOrgSaving(false);
    }
  };

  const saveProperty = async () => {
    if (!selectedProperty) return;
    setPropSaving(true);
    try {
      const res = await settingsApi.updatePropertyInvoiceMatching(selectedProperty, {
        propertyId: selectedProperty,
        overrideInvoiceMatching: propOverride,
        matchPriceTolerancePercent: toPayload(propPrice),
        matchQuantityTolerancePercent: toPayload(propQty),
        requireInvoiceAttachmentOnApprove: propRequireAttachment,
      });
      setPropOverride(!!res?.overrideInvoiceMatching);
      setPropPrice(fromDto(res?.matchPriceTolerancePercent));
      setPropQty(fromDto(res?.matchQuantityTolerancePercent));
      setPropRequireAttachment(res?.requireInvoiceAttachmentOnApprove !== false);
      toast.success(t('Property overrides saved'));
    } catch (err) {
      toast.error(t('Could not save overrides'), { description: err?.message });
    } finally {
      setPropSaving(false);
    }
  };

  if (orgLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <section>
        <h2 className="appr-scope__title">{t('Organization defaults')}</h2>
        <Card>
          <Card.Body>
            <div className="set-section__head">
              <div>
                <h3 className="set-section__title">{t('Three-way match tolerances')}</h3>
                <p className="set-section__hint">
                  {t('Every invoice is checked against what was ordered (PO price) and what was received (goods receipts). Differences within these tolerances still count as a match; anything beyond gets flagged as a variance and forces the full invoice approval chain.')}
                </p>
              </div>
            </div>
            <ToleranceFields pricePct={orgPrice} qtyPct={orgQty} onPrice={setOrgPrice} onQty={setOrgQty} t={t} />
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Switch
                checked={orgRequireAttachment}
                onChange={setOrgRequireAttachment}
                label={t('Require the scanned supplier invoice to be attached before an invoice can be approved')}
              />
            </div>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button variant="primary" size="sm" loading={orgSaving} onClick={saveOrg}>{t('Save')}</Button>
            </div>
          </Card.Body>
        </Card>
      </section>

      <section>
        <h2 className="appr-scope__title">{t('Property overrides')}</h2>
        {!propsLoaded ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
        ) : properties.length === 0 ? (
          <EmptyState
            icon={<Icon.Building size={28} />}
            title={t('No properties yet')}
            description={t('Once a property exists, you can override the organization match tolerances for it.')}
          />
        ) : (
          <>
            <div className="set-property-pick">
              <Select
                options={[{ value: '', label: t('Select a property…') }, ...properties.map((p) => ({ value: p.id, label: p.name }))]}
                value={selectedProperty}
                onChange={loadProperty}
                ariaLabel={t('Property')}
                searchable
              />
            </div>

            {!selectedProperty ? (
              <Banner tone="info">{t('Choose a property to override its match tolerances. Left as inherit, it uses the organization defaults.')}</Banner>
            ) : propLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
            ) : propLoaded ? (
              <Card>
                <Card.Body>
                  <div className="set-section__head">
                    <div>
                      <h3 className="set-section__title">{t('Property override')}</h3>
                      <p className="set-section__hint">{t('When off, this property inherits the organization match tolerances.')}</p>
                    </div>
                    <Switch checked={propOverride} onChange={setPropOverride} label={t('Override')} />
                  </div>
                  {propOverride ? (
                    <>
                      <ToleranceFields pricePct={propPrice} qtyPct={propQty} onPrice={setPropPrice} onQty={setPropQty} t={t} />
                      <div style={{ marginTop: 'var(--space-4)' }}>
                        <Switch
                          checked={propRequireAttachment}
                          onChange={setPropRequireAttachment}
                          label={t('Require the scanned supplier invoice to be attached before an invoice can be approved')}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="set-inherit" style={{ marginTop: 'var(--space-4)' }}>
                      {t('Inherits the organization match tolerances.')}
                    </p>
                  )}
                  <div style={{ marginTop: 'var(--space-4)' }}>
                    <Button variant="primary" size="sm" loading={propSaving} onClick={saveProperty}>{t('Save')}</Button>
                  </div>
                </Card.Body>
              </Card>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
